import { useState } from "react";
// --------------------- Import Services ---------------------
import { cadastrarUsuarioService } from "../../../services/cadastrarUsuarioService";
import { cadastrarEnderecoService } from "../../../services/cadastrarEnderecoService";
import { atualizarCotaUsuarioService } from "../../../services/atualizarCotaUsuarioService";
import { buscarUsuarioByCelService } from "../../../services/buscarUsuarioByCelService";

// --------------------- Interfaces ---------------------
import { Usuario } from "../../../interfaces/Usuario";
import { Endereco } from "../../../interfaces/Endereco";
import { CotaUsuario } from "../../../interfaces/CotaUsuario";
import { Representante } from "../../../interfaces/Representante";
import { atualizarRepresentante } from "../../../services/atualizarRepresentanteService";

// --------------------- Úteis ---------------------
import { numberPadrao } from "../../../uteis/formatPhoneNumber";
import { buscarRepresentanteByCotaUsuarioService } from "../../../services/buscarRepresentanteByCotaUsuarioService";
import { buscarCotaUsuarioByIdService } from "../../../services/buscarCotaUsuarioByIdService";

interface UsuarioData {
  nome: string;
  sobrenome: string;
  cpf: string;
  rg: string;
  celular: string;
  nacionalidade: string;
  estadoCivil: string;
  endereco: string;
  complemento: string;
  estadoId: number;
  cidadeId: number;
  paisId: number;
}

export const useAdicionarProprietario = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetError = () => setError(null);
  const resetSuccess = () => setSuccess(false);

  // --------------------- Lógica Auxiliar ---------------------
  const obterOuCriarUsuario = async (usuarioData: Usuario) => {
    if (!usuarioData?.celular) {
      const msg =
        "Celular é obrigatório para buscar ou cadastrar proprietário.";
      setError(msg);
      throw new Error(msg);
    }

    const usuarioExistente = await buscarUsuarioByCelService(
      usuarioData.celular
    );
    if (usuarioExistente) return usuarioExistente;

    return cadastrarUsuarioService(usuarioData);
  };

  const buscarRepresentante = async (cotaUsuarioId: number) => {
    return await buscarRepresentanteByCotaUsuarioService(cotaUsuarioId);
  };

  const cadastrarEndereco = async (usuarioId: number, data: UsuarioData) => {
    const enderecoData: Endereco = {
      endereco: data.endereco,
      complemento: data.complemento,
      cidadeId: data.cidadeId,
      estadoId: data.estadoId,
      paisId: data.paisId,
      usuarioId,
    };

    const endereco = await cadastrarEnderecoService(enderecoData);

    if (!endereco) {
      throw new Error("Erro ao cadastrar endereço.");
    }
    return endereco;
  };

  const atualizarCotaUsuario = async (
    cotasUsuariosId: number,
    usuarioId: number
  ) => {
    const cotasUsuariosData: Partial<CotaUsuario> = { usuarioId };
    return atualizarCotaUsuarioService(cotasUsuariosId, cotasUsuariosData);
  };

  // --------------------- Fluxo principal ---------------------
  const adicionar = async (
    representanteData: Representante,
    usuarioData: UsuarioData
  ) => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Buscar ou cadastrar proprietário
      const usuario = await obterOuCriarUsuario({
        ...usuarioData,
        celular: numberPadrao(usuarioData.celular),
      });

      // Buscar representante
      const representante = await buscarRepresentante(
        representanteData.cotasUsuariosId!
      );

      // Cadastrar endereço do proprietário
      await cadastrarEndereco(usuario.id, usuarioData);

      // Busca a cota usuario
      const cotaUsuario = await buscarCotaUsuarioByIdService(
        representanteData.cotasUsuariosId!
      );

      // Atualiza a cota usuario com o novo proprietário
      await atualizarCotaUsuario(
        representanteData.cotasUsuariosId!,
        usuario.id
      );

      // Atualiza o representante com o antigo proprietario
      const representanteDataNovo = {
        ...representanteData,
        representanteId: representante.id,
        usuarioId: cotaUsuario.usuarioId,
      };

      await atualizarRepresentante(representanteDataNovo);

      setSuccess(true);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Erro ao adicionar proprietário.");
    } finally {
      setIsLoading(false);
    }
  };

  return { adicionar, isLoading, error, success, resetError, resetSuccess };
};
